import { useTranslate } from '@refinedev/core';
import type { CrudSort } from '@refinedev/core';
import type { TableProps } from 'antd';
import { Table, Tag } from 'antd';
import type { WithdrawalContractOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { getSortOrder } from '@/utils/sorters';
import { WaiverStatusTag } from './WaiverStatusTag';

interface WithdrawalContractsTableProps {
  tableProps: TableProps<WithdrawalContractOut>;
  sorters: CrudSort[];
}

const RESOURCE = 'monetization.withdrawal_contracts';

/** Compact withdrawal-contracts table for embedding on related show views. */
export const WithdrawalContractsTable = ({
  tableProps,
  sorters,
}: WithdrawalContractsTableProps) => {
  const t = useTranslate();
  const f = (key: string) =>
    t(`monetization.withdrawal_contracts.fields.${key}`);

  return (
    <Table<WithdrawalContractOut>
      {...tableProps}
      rowKey="id"
      size="small"
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: t('messages.no_data') }}
    >
      <Table.Column
        dataIndex="id"
        title={t('base.fields.id')}
        sorter
        sortOrder={getSortOrder(sorters, 'id')}
        render={(value: number) => <ShowLink resource={RESOURCE} id={value} />}
      />
      <Table.Column
        dataIndex="waiver_id"
        title={f('waiver_status')}
        sorter
        sortOrder={getSortOrder(sorters, 'waiver_id')}
        render={(_: unknown, record: WithdrawalContractOut) => (
          <WaiverStatusTag
            waiverId={record.waiver_id}
            fingerprintValid={record.consent_fingerprint_valid}
            waivedAt={record.waived_at}
          />
        )}
      />
      <Table.Column
        dataIndex="event_type"
        title={f('event_type')}
        sorter
        sortOrder={getSortOrder(sorters, 'event_type')}
        render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
      />
      <Table.Column
        dataIndex="store"
        title={f('store')}
        sorter
        sortOrder={getSortOrder(sorters, 'store')}
        render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
      />
      <Table.Column
        dataIndex="product_id"
        title={f('product_id')}
        sorter
        sortOrder={getSortOrder(sorters, 'product_id')}
        render={(value: string | null, record: WithdrawalContractOut) => (
          <>
            {value ?? '-'}
            {record.new_product_id && record.new_product_id !== value && (
              <> → {record.new_product_id}</>
            )}
          </>
        )}
      />
      <Table.Column
        dataIndex="consent_version"
        title={f('consent_version')}
        render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
      />
      <Table.Column
        dataIndex="transaction_id"
        title={f('transaction_id')}
        render={(value: string | null) =>
          value ? <span style={{ fontFamily: 'monospace' }}>{value}</span> : '-'
        }
      />
      <Table.Column
        dataIndex="event_timestamp"
        title={f('event_timestamp')}
        sorter
        sortOrder={getSortOrder(sorters, 'event_timestamp')}
        render={(value: string | null) =>
          value ? new Date(value).toLocaleString() : '-'
        }
      />
      <Table.Column
        dataIndex="waived_at"
        title={f('waived_at')}
        sorter
        sortOrder={getSortOrder(sorters, 'waived_at')}
        render={(value: string | null) =>
          value ? new Date(value).toLocaleString() : '-'
        }
      />
    </Table>
  );
};
